import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings } from 'lucide-react';
import LayoutWithChat from './LayoutWithChat';
import { fetchLeagues, League } from '../api/leagues';
import ResponsiveTable, { Column } from './ResponsiveTable';

const LeaguesPage: React.FC = () => {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLeagues();
  }, []);

  const loadLeagues = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetchLeagues();
      setLeagues(response.leagues || []);
    } catch (err: any) {
      if (err.message === 'Authentication expired') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to load leagues');
      }
    } finally {
      setIsLoading(false);
    }
  };


  // Group leagues by season (year)
  const leaguesBySeason = leagues.reduce((acc, league) => {
    if (!acc[league.season]) {
      acc[league.season] = [];
    }
    acc[league.season].push(league);
    return acc;
  }, {} as Record<string, League[]>);

  // Sort seasons in descending order
  const seasons = Object.keys(leaguesBySeason).sort((a, b) => b.localeCompare(a));

  const getStatusBadge = (league: League) => {
    const statusColors = {
      'pre-draft': 'bg-blue-400/20 text-blue-400',
      'drafting': 'bg-yellow-400/20 text-yellow-400',
      'in-season': 'bg-green-400/20 text-green-400',
      'post-season': 'bg-purple-400/20 text-purple-400',
      'finished': 'bg-gray-400/20 text-gray-300'
    };

    const statusText = league.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
    const colorClass = statusColors[league.status] || 'bg-gray-400/20 text-gray-300';

    return (
      <span className={`inline-block ${colorClass} text-xs font-semibold px-2 py-1 rounded-full`}>
        {statusText}
      </span>
    );
  };

  const getFormatText = (league: League) => {
    if (!league.leagueFormat) {
      return '-';
    }

    return league.leagueFormat.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const columns: Column[] = [
    {
      key: 'name',
      label: 'League Name',
      width: '30%',
      className: 'text-sm font-medium text-text-primary',
      render: (value) => (
        <div className="max-w-[150px] sm:max-w-none truncate" title={value}>
          {value}
        </div>
      )
    },
    {
      key: 'leagueFormat',
      label: 'Format',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value, league) => getFormatText(league)
    },
    {
      key: 'platform',
      label: 'Platform',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-'
    },
    {
      key: 'teamCount',
      label: 'Teams',
      width: '15%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value) => `${value} teams`
    },
    {
      key: 'currentWeek',
      label: 'Week',
      width: '10%',
      className: 'whitespace-nowrap text-sm text-text-secondary',
      render: (value, league) => league.currentWeek ? `${league.currentWeek}/${league.totalWeeks}` : '-'
    },
    {
      key: 'status',
      label: 'Status',
      width: '15%',
      className: 'whitespace-nowrap text-sm',
      render: (value, league) => getStatusBadge(league)
    }
  ];

  if (isLoading) {
    return (
      <LayoutWithChat 
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-text-secondary">Loading leagues...</div>
        </div>
      </LayoutWithChat>
    );
  }

  if (error) {
    return (
      <LayoutWithChat 
      >
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="text-red-400">{error}</div>
          <button
            onClick={loadLeagues}
            className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary text-sm font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </LayoutWithChat>
    );
  }

  if (leagues.length === 0) {
    return (
      <LayoutWithChat 
      >
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="text-text-secondary">No leagues found.</div>
          <button
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-2 bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary text-sm font-medium py-2 px-4 rounded-lg transition-colors"
          >
            <Settings size={16} />
            Connect Your Fantasy Accounts
          </button>
        </div>
      </LayoutWithChat>
    );
  }

  return (
    <LayoutWithChat>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-normal text-text-primary">My Leagues</h1>
        </div>
        
        {seasons.map((season) => (
          <div key={season}>
            {/* Season Divider */}
            <div className="flex items-center my-6">
              <div className="flex-grow border-t border-border-primary"></div>
              <span className="px-4 text-text-secondary text-lg font-normal">{season}</span>
              <div className="flex-grow border-t border-border-primary"></div>
            </div>
            
            <ResponsiveTable
              columns={columns}
              data={leaguesBySeason[season]}
              keyExtractor={(league) => league.id}
              onRowClick={(league) => navigate(`/league/${league.id}`)}
              mobileColumns={['name', 'leagueFormat', 'platform', 'teamCount', 'currentWeek']}
              cardRenderer={(league) => (
                <div
                  onClick={() => navigate(`/league/${league.id}`)}
                  className="bg-bg-secondary border border-border-primary rounded-lg p-4 cursor-pointer hover:bg-bg-tertiary transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-base font-medium text-text-primary pr-2">
                      {league.name}
                    </h3>
                    {getStatusBadge(league)}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Format</div>
                      <div className="text-text-primary">{getFormatText(league)}</div>
                    </div>
                    <div>
                      <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Platform</div>
                      <div className="text-text-primary">{league.platform.charAt(0).toUpperCase() + league.platform.slice(1)}</div>
                    </div>
                    <div>
                      <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Teams</div>
                      <div className="text-text-primary">{league.teamCount} teams</div>
                    </div>
                    <div>
                      <div className="text-text-secondary text-xs uppercase tracking-wider mb-1">Week</div>
                      <div className="text-text-primary">{league.currentWeek ? `${league.currentWeek}/${league.totalWeeks}` : '-'}</div>
                    </div>
                  </div>
                </div>
              )}
              className="mb-8"
            />
          </div>
        ))}
      </div>
    </LayoutWithChat>
  );
};

export default LeaguesPage;